/**
 * Minimal WAV encoder: AudioBuffer -> 16-bit PCM RIFF/WAVE Blob.
 * Used by the offline FX renderer so processed audio can play through a
 * plain <audio> element (native pipeline — safe for CarPlay/Bluetooth on iOS).
 *
 * IMPORTANT: `encodeWavFromChannels` is deliberately SELF-CONTAINED (no free
 * variables) so fxPostProcess.js can serialize it into a Web Worker via
 * Function.toString — the per-sample encode loop (tens of millions of
 * DataView writes for a long track) must not block the main thread on old
 * devices. Keep any helpers inlined in its body.
 */

/**
 * Encode an AudioBuffer as a 16-bit PCM WAV Blob.
 * @param {AudioBuffer} audioBuffer
 * @returns {Blob} audio/wav blob
 */
export function encodeWavBlob(audioBuffer) {
  return new Blob([encodeWavArrayBuffer(audioBuffer)], { type: 'audio/wav' });
}

/**
 * Encode an AudioBuffer as a 16-bit PCM WAV ArrayBuffer (header + data).
 * @param {AudioBuffer} audioBuffer
 * @returns {ArrayBuffer}
 */
export function encodeWavArrayBuffer(audioBuffer) {
  const channels = [];
  for (let ch = 0; ch < audioBuffer.numberOfChannels; ch++) {
    channels.push(audioBuffer.getChannelData(ch));
  }
  return encodeWavFromChannels(channels, audioBuffer.sampleRate, audioBuffer.length);
}

/**
 * Self-contained core: encode raw channel data as a 16-bit PCM WAV.
 * Safe to run inside a Web Worker (no module-scope references).
 *
 * @param {Float32Array[]} channels - one Float32Array per channel
 * @param {number} sampleRate
 * @param {number} numFrames
 * @returns {ArrayBuffer}
 */
export function encodeWavFromChannels(channels, sampleRate, numFrames) {
  const numChannels = channels.length;

  const bytesPerSample = 2; // 16-bit
  const blockAlign = numChannels * bytesPerSample;
  const byteRate = sampleRate * blockAlign;
  const dataSize = numFrames * blockAlign;

  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);

  const writeString = (offset, str) => {
    for (let i = 0; i < str.length; i++) {
      view.setUint8(offset + i, str.charCodeAt(i));
    }
  };

  // RIFF header
  writeString(0, 'RIFF');
  view.setUint32(4, 36 + dataSize, true); // file size - 8
  writeString(8, 'WAVE');

  // fmt chunk
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true); // fmt chunk size
  view.setUint16(20, 1, true); // PCM format
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, 16, true); // bits per sample

  // data chunk
  writeString(36, 'data');
  view.setUint32(40, dataSize, true);

  // Interleave channels and convert Float32 [-1, 1] -> Int16
  let offset = 44;
  for (let frame = 0; frame < numFrames; frame++) {
    for (let ch = 0; ch < numChannels; ch++) {
      let sample = channels[ch][frame];
      // Clamp to [-1, 1] (compressor makeup gain can push peaks over 0 dBFS)
      if (sample > 1) sample = 1;
      else if (sample < -1) sample = -1;
      view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
      offset += 2;
    }
  }

  return buffer;
}
